import 'dayjs/locale/fr';
import { useEffect } from 'react';
import { BirdIcon, EnvelopeIcon, MoonStarsIcon, SunIcon } from '@phosphor-icons/react';
import { DARK, LIGHT } from '@protomaps/basemaps';
import { createRoot } from 'react-dom/client';
import {
  ActionIcon,
  AppShell,
  createTheme,
  Group,
  MantineProvider,
  Title,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { NavigationProgress } from '@mantine/nprogress';
import SightingsMap from './mapgl';
import { useStore } from './store';
import '../assets/app.css';

const theme = createTheme({
  primaryShade: { light: 7, dark: 7 },
  fontFamily: 'Jost',
});

const ColorSchemeButton = () => {
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('dark', { getInitialValueInEffect: true });

  return (
    <ActionIcon
      variant="outline"
      color={computedColorScheme === 'dark' ? 'yellow' : 'blue'}
      onClick={() => setColorScheme(computedColorScheme === 'dark' ? 'light' : 'dark')}
      title="Toggle color scheme"
    >
      {computedColorScheme === 'dark' ? <SunIcon size="1.0rem" /> : <MoonStarsIcon size="1.0rem" />}
    </ActionIcon>
  );
};

const App = () => {
  const computedColorScheme = useComputedColorScheme('dark', { getInitialValueInEffect: true });
  const loadStats = useStore((state) => state.loadStats);
  const setTheme = useStore((state) => state.setTheme);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    setTheme(computedColorScheme === 'dark' ? DARK : LIGHT);
  }, [computedColorScheme]);

  return (
    <AppShell
      header={{ height: 48 }}
      padding={0}
      styles={() => ({
        main: {
          display: 'flex',
          flexDirection: 'column',
        },
      })}
    >
      <AppShell.Header withBorder>
        <Group h="100%" px="md">
          <ActionIcon variant="outline">
            <BirdIcon size="2.0rem" />
          </ActionIcon>
          <Title order={2}>Cartofaune</Title>
          <ActionIcon
            component="a"
            href="mailto:shagr4th@gmail.com"
            style={{ marginLeft: 'auto' }}
            variant="outline"
            title="Contact par e-mail"
          >
            <EnvelopeIcon size="1.0rem" />
          </ActionIcon>
          <ColorSchemeButton />
        </Group>
      </AppShell.Header>
      <AppShell.Main>
        <SightingsMap />
      </AppShell.Main>
    </AppShell>
  );
};

// Prévention auto zoom dans les inputs sur iOS : https://stackoverflow.com/a/57527009
// https://stackoverflow.com/questions/2989263/disable-auto-zoom-in-input-text-tag-safari-on-iphone
if (navigator && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
  const meta = document.querySelector('meta[name=viewport]');
  if (meta !== null) {
    let content = meta.getAttribute('content') || '';
    const regex = /maximum-scale=[0-9.]+/g;
    if (regex.test(content)) {
      content = content.replace(regex, 'maximum-scale=1.0');
    } else {
      content = [content, 'maximum-scale=1.0'].join(', ');
    }
    meta.setAttribute('content', content);
  }
}

createRoot(document.body).render(
  <MantineProvider theme={theme} defaultColorScheme="auto">
    <DatesProvider settings={{ locale: 'fr' }}>
      <NavigationProgress />
      <App />
    </DatesProvider>
  </MantineProvider>
);
